import React from 'react';
import { clsx } from 'clsx';

import { useRovingTabs } from './use-roving-tabs';

/**
 * Generic folder/card tab strip (LR-587).
 *
 * The visual language of the Mark to Market counterparty subtabs
 * (`features/trades/components/mtm/MtmCounterpartyTabs.tsx`): the active tab is
 * a white card with top/side borders and a rounded top, sitting on an INSET
 * bottom divider so its body covers the line and reads as connected to the
 * content below. Extracted the moment the third copy appeared (the collapse the
 * ProductTabs NOTE scheduled for LR-587): the GMRA/GMSLA segment of the pool
 * register and the collateral-leg strip of the Collateral Manager both render
 * through it.
 *
 * Roving tabindex + arrow navigation (WAI-ARIA tabs): one tab stop into the
 * strip, arrows move focus AND selection.
 */

const TAB_BUTTON_CLASSES =
  'px-4 py-2.5 text-sm font-medium motion-safe:transition-colors whitespace-nowrap cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

export interface FolderTabItem<K extends string> {
  key: K;
  label: string;
}

interface FolderTabsProps<K extends string> {
  tabs: readonly FolderTabItem<K>[];
  active: K;
  onChange: (key: K) => void;
  ariaLabel: string;
  /** Prefix for the per-tab DOM ids (`${idPrefix}-${key}`). */
  idPrefix: string;
  /**
   * DOM id of the panel this strip switches. Give it one whenever the strip has a
   * panel of its own: without `aria-controls` a screen-reader user who arrows
   * along the tabs is never told what changed, and has no route from the panel
   * back to the tabs that chose it.
   */
  panelId?: string;
}

export const FolderTabs = <K extends string>({
  tabs,
  active,
  onChange,
  ariaLabel,
  idPrefix,
  panelId,
}: FolderTabsProps<K>): React.ReactElement => {
  const { refs: tabRefs, onKeyDown: handleKeyDown } = useRovingTabs(
    tabs.map((tab) => tab.key),
    onChange,
  );

  return (
    // -mx-6 px-6 cancels CardHeader's padding so the divider runs the full
    // width, matching the MTM strip.
    <div
      className="scroll-x-thin -mx-6 flex items-center gap-1 overflow-x-auto overflow-y-hidden px-6 shadow-[inset_0_-1px_0_0_#cbd5e1]"
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`${idPrefix}-${tab.key}`}
            // The strip owns its OWN panel when one is given: nesting it inside
            // the outer tablist's panel keeps each group with a single owner and
            // still tells a screen reader what these tabs switch. `aria-current`
            // is deliberately absent - `page` is for navigation landmarks, and
            // `aria-selected` already carries a tab's state.
            aria-controls={panelId}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onClick={() => onChange(tab.key)}
            className={clsx(
              TAB_BUTTON_CLASSES,
              isActive
                ? 'relative z-10 rounded-t-lg border-x border-t border-slate-300 bg-white font-semibold text-slate-900'
                : 'rounded-md bg-transparent text-slate-500 hover:bg-slate-200 hover:text-slate-800'
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
